import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/api';

const greeting = (label) => ({
  role: 'assistant',
  content: `已准备好测试 ${label} 的当前草稿。发送一条消息即可验证联通状态。`
});

const ConnectionTestPanel = ({ targets, canWrite, selectedTargetKey, onTargetChange }) => {
  const availableTargets = useMemo(() => Array.isArray(targets) ? targets : [], [targets]);
  const [activeTargetKey, setActiveTargetKey] = useState(selectedTargetKey || '');
  const [messages, setMessages] = useState([]);
  const [draftMessage, setDraftMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [latency, setLatency] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const activeTarget = availableTargets.find(target => target.key === activeTargetKey) || availableTargets[0];
  const hasResponse = messages.some(message => message.role === 'assistant') && messages.some(message => message.role === 'user');

  useEffect(() => {
    const nextKey = availableTargets.some(target => target.key === selectedTargetKey) ? selectedTargetKey : (availableTargets[0]?.key || '');
    setActiveTargetKey(nextKey);
  }, [availableTargets, selectedTargetKey]);

  useEffect(() => {
    setMessages(activeTarget ? [greeting(activeTarget.label)] : []);
    setSessionId(null);
    setLatency(null);
    setError('');
  }, [activeTarget]);

  const selectTarget = (key) => {
    setActiveTargetKey(key);
    onTargetChange?.(key);
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const content = draftMessage.trim();
    if (!content || !activeTarget || sending) return;

    const nextMessages = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setDraftMessage('');
    setSending(true);
    setError('');

    try {
      const response = await api.testAIChat({
        target_type: activeTarget.type,
        target_id: String(activeTarget.targetId),
        api_key: activeTarget.apiKey || undefined,
        draft: activeTarget.draft,
        messages: nextMessages,
        session_id: sessionId || undefined
      });
      const result = response.data || {};
      setMessages(current => [...current, { role: 'assistant', content: result.reply || '服务未返回可显示的文本。' }]);
      setSessionId(result.session_id || null);
      setLatency(typeof result.latency_ms === 'number' ? result.latency_ms : null);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || '联通测试失败');
    } finally {
      setSending(false);
    }
  };

  const status = sending ? '联通中' : error ? '连接失败' : hasResponse ? '已连接' : '等待测试';
  const testDescription = activeTarget?.type === 'agent' ? '验证 Agent 的当前草稿' : '验证当前提供商的草稿';

  return (
    <aside className="connection-test-panel" aria-label="联通测试">
      <div className="ai-card-head ai-chat-head">
        <div className="ai-card-title"><span className="ai-icon-box">⌁</span><div><h2>联通测试</h2><p>{testDescription}</p></div></div>
        <span className={`ai-chat-status ${error ? 'is-error' : ''}`}>{status}</span>
      </div>
      <div className="connection-test-body">
        {!canWrite ? <div className="connection-test-permission">需要 AI 设置编辑权限才能发起联通测试。</div> : availableTargets.length === 0 ? <div className="connection-test-empty">当前没有可测试的配置。请先保存并启用一个 Agent，或切换到 AI 提供商。</div> : <>
          <div className="connection-test-context">
            <i />测试目标 <strong>{activeTarget?.label}</strong>
            <label className="connection-test-target-switch">切换 ▾<select aria-label="测试目标" value={activeTargetKey} onChange={(event) => selectTarget(event.target.value)}>{availableTargets.map(target => <option key={target.key} value={target.key}>{target.label}</option>)}</select></label>
          </div>

          <div className="connection-test-conversation" aria-live="polite">
            {messages.map((message, index) => <div key={`${message.role}-${index}`} className={`connection-test-message connection-test-message--${message.role}`}><span className="connection-test-message-meta">{message.role === 'user' ? '你' : `${activeTarget?.label || 'AI'}${latency !== null && message.role === 'assistant' && index === messages.length - 1 ? ` · ${Math.max(latency / 1000, 0.1).toFixed(1)} 秒` : ''}`}</span>{message.content}</div>)}
            {sending && <div className="connection-test-message connection-test-message--assistant"><span className="connection-test-message-meta">{activeTarget?.label || 'AI'}</span>正在连接服务…</div>}
          </div>

          {hasResponse && !error && <div className="connection-test-result"><b>✓</b><div><strong>联通成功</strong><br />{activeTarget?.label} 响应正常{latency !== null ? ` · ${Math.max(latency / 1000, 0.1).toFixed(1)} 秒` : ''}。测试不会创建或修改 Jira 数据。</div></div>}
          {error && <div className="connection-test-error" role="alert"><b>!</b><div><strong>联通失败</strong><br />{error}</div></div>}

          <form className="connection-test-composer" onSubmit={sendMessage}>
            <textarea rows={2} value={draftMessage} onChange={(event) => setDraftMessage(event.target.value)} placeholder="输入一条测试消息…" disabled={sending} />
            <button className="btn btn-primary" type="submit" disabled={sending || !draftMessage.trim()}>{sending ? '发送中…' : '发送'}</button>
          </form>
          <p className="connection-test-footnote">切换目标即可测试任意已启用 {activeTarget?.type === 'agent' ? 'Agent' : '提供商'}。</p>
        </>}
      </div>
    </aside>
  );
};

export default ConnectionTestPanel;
